import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import PropTypes from 'prop-types';
import { Form, Field } from 'react-final-form';
import { Modal, Button, SelectField, TextField } from '@unite-us/app-components';
import { REQUEST_UNASSIGNED } from 'common/Workqueue/hooks/useAssigneeOptions';

export const InReviewModal = forwardRef(({
  title = 'Mark as In Review',
  onSubmit,
  assigneeOptions,
  reviewReasonOptions,
  initialFormValues = {},
}, ref) => {
  const modalRef = useRef(null);
  const formRef = useRef(null);
  const modalInitialValues = {
    ...initialFormValues,
    assignee: initialFormValues?.assignee || REQUEST_UNASSIGNED,
  };

  const openModal = () => {
    modalRef.current.openModal();
  };

  const closeModal = () => {
    if (formRef.current) {
      formRef.current.reset();
    }
    // eslint-disable-next-line no-unused-expressions
    modalRef?.current?.closeModal();
  };

  useImperativeHandle(ref, () => ({
    openModal,
    closeModal,
  }));

  const validateForm = (values) => {
    const errors = {};
    if (!values.review_reason) {
      errors.review_reason = 'Review reason is required';
    }
    return errors;
  };

  const handleFormSubmit = async (values) => {
    await onSubmit(values);
    closeModal();
  };

  return (
    <Modal
      ref={modalRef}
      title={title}
      size="large"
      modal
      showBorderRadius
      onRequestClose={closeModal}
      titleStyles="font-semibold text-md"
      disableScroll
      modalContentStyles="overflow-visible"
    >
      <Form
        initialValues={modalInitialValues}
        onSubmit={handleFormSubmit}
        validate={validateForm}
        render={({
          handleSubmit,
          submitting,
          pristine,
          hasValidationErrors,
          form,
        }) => {
          formRef.current = form;

          return (
            <form onSubmit={handleSubmit}>
              <div className="space-y-6 position-auto overflow-visible max-h-96 overflow-y-auto mb-24">
                <Field name="review_reason">
                  {({ input, meta }) => (
                    <div>
                      <SelectField
                        id="review-reason"
                        label="Reason for Review"
                        placeholder="Select a review reason"
                        options={reviewReasonOptions}
                        required
                        invalid={meta.touched && !!meta.error}
                        error={meta.touched && meta.error}
                        position="bottom"
                        labelClassName="font-semibold"
                        {...input}
                      />
                    </div>
                  )}
                </Field>

                <Field name="in_review_note">
                  {({ input, meta }) => (
                    <div>
                      <TextField
                        id="in-review-note"
                        label="Additional Notes"
                        placeholder="Add any additional notes..."
                        rows={3}
                        invalid={meta.touched && !!meta.error}
                        error={meta.touched && meta.error}
                        {...input}
                      />
                    </div>
                  )}
                </Field>

                <Field name="assignee">
                  {({ input, meta }) => (
                    <div>
                      <SelectField
                        id="assignee"
                        label="Assign User"
                        placeholder="Pick one"
                        options={assigneeOptions}
                        invalid={meta.touched && !!meta.error}
                        error={meta.touched && meta.error}
                        position="bottom"
                        labelClassName="font-semibold"
                        {...input}
                      />
                    </div>
                  )}
                </Field>
              </div>

              <div className="flex justify-end gap-4 mt-6">
                <Button
                  label="Cancel"
                  onClick={closeModal}
                  secondary
                />
                <Button
                  label="Save"
                  type="submit"
                  primary
                  disabled={submitting || pristine || hasValidationErrors}
                  icon={{
                    position: 'left',
                    name: 'Save',
                    color: '#FFFFFF',
                  }}
                />
              </div>
            </form>
          );
        }}
      />
    </Modal>
  );
});

InReviewModal.propTypes = {
  title: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  assigneeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  reviewReasonOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
  initialFormValues: PropTypes.shape({
    review_reason: PropTypes.string,
    in_review_note: PropTypes.string,
    assignee: PropTypes.string,
  }),
};

InReviewModal.defaultProps = {
  title: 'Mark as In Review',
  initialFormValues: {},
};
